import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { useAnchoredPlacement } from "./useAnchoredPlacement";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Award, CirclePause, Compass, Eye, FlaskConical, Keyboard, LogOut, Monitor, PenOff, Smartphone, Sparkle, Square, Type, Undo2, UserRound, type LucideIcon } from "lucide-react";
import { togglePhoneLayout, usePhoneLayoutPreferred } from "../../lib/phone/phoneLayout";
import { toggleLargeText, useLargeText } from "../../lib/textSize";
import { readViewAs, stopViewAs, useViewAs } from "../../lib/viewAs/viewAs";
import { HEADER_QK, headerApi } from "../../api/headerApi";
import { usePrincipal } from "../../auth/AuthContext";
import { Roles } from "../../auth/roles";
import { screensFor } from "../hub/hubs";
import { usePersonalReduceMotion, writeReduceMotionPreference } from "../motion/motionPreference";
import { backToAdmin, ViewAsBanner } from "../viewAs/ViewAsBanner";
import { ViewAsDialog } from "../viewAs/ViewAsDialog";
import { stopTestAs, useTestAsStatus } from "../../lib/testAs/testAs";
import { TestAsBanner } from "../testAs/TestAsBanner";
import { TestAsDialog } from "../testAs/TestAsDialog";
import { Avatar } from "./Avatar";
import { openHeaderPanel } from "./headerStore";
import { HeaderTour, readTourProgress, startTour, tourLabel, tourLength } from "./HeaderTour";
import { TourOffer } from "./TourOffer";

// Release 1.5.0 header account menu, ported from Summit Engineering's AccountMenu: a circle avatar replaces the name/role
// text and opens the menu — the guided tour, keyboard shortcuts and release notes first, then the profile, then the
// session: "End signing session" (only while this sign-in holds an authenticator proof; it ends the proof on the
// server, so the next signed step asks for a code again) and "Sign out". WAI-ARIA menu button: Enter/Space/↓ opens and
// focuses the first item, ↑↓ Home End move, Esc closes and returns focus, Tab leaves.

interface Item {
  key: string;
  label: string;
  icon: LucideIcon;
  run: () => void;
  group: number;
  tone?: "danger";
  /** A switch (menuitemcheckbox): on or off. */
  checked?: boolean;
}

export function AccountMenu({
  name,
  roleLabel,
  roles,
  upn,
  onSignOut
}: {
  name: string;
  roleLabel: string;
  roles: readonly string[];
  upn?: string | null;
  onSignOut: () => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const place = useAnchoredPlacement(buttonRef, open || notice !== null, 288);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const menuId = useId();
  // D-O115: while an Admin views as a role, the tour is that role's tour and its progress is kept apart.
  const viewAs = useViewAs();
  const [viewAsOpen, setViewAsOpen] = useState(false);
  // O-R165–O-R170: the Admin's "Test as role" (real actions, signed by them). Off while viewing as a role.
  const canTestAs = roles.includes(Roles.Admin) && !viewAs;
  const testAsStatus = useTestAsStatus(canTestAs);
  const testing = canTestAs && testAsStatus.data?.active ? testAsStatus.data : null;
  const [testAsOpen, setTestAsOpen] = useState(false);
  const assignedRoles = usePrincipal()?.assignedRoles;
  const motionPerson = upn ?? name;
  const person = viewAs ? `${upn ?? name}#as-${viewAs.role}` : (upn ?? name);
  const tourTotal = tourLength(roles, assignedRoles);
  const reduceMotion = usePersonalReduceMotion();
  // The step-up status is read when the menu opens (and kept a minute), never polled.
  const signing = useQuery({ queryKey: [...HEADER_QK.stepUp], queryFn: headerApi.signingSession, enabled: open, staleTime: 60_000, retry: false });
  const canEndSigning = !!signing.data && signing.data.required && signing.data.verified && !signing.data.bypassed;
  const screens = screensFor(roles).map((s) => s.to);
  const photo = roles.some((r) => r !== Roles.WallDisplay);
  // Release 1.5.0 phone mode (phone-1-5): the explicit "Phone layout" switch, kept on this device.
  const phoneLayout = usePhoneLayoutPreferred();
  const largeText = useLargeText();

  const items: Item[] = [];
  items.push({ key: "tour", label: tourLabel(readTourProgress(person), tourTotal), icon: Compass, run: () => startTour(person, readTourProgress(person).step === null), group: 0 });
  items.push({ key: "shortcuts", label: "Keyboard shortcuts", icon: Keyboard, run: () => openHeaderPanel("shortcuts"), group: 0 });
  items.push({
    key: "phone-layout",
    label: phoneLayout ? "Use the desktop layout" : "Use the phone layout",
    icon: phoneLayout ? Monitor : Smartphone,
    run: () => togglePhoneLayout(phoneLayout),
    group: 0
  });
  // Release 1.5.0 polish: larger text (115 %), kept on this device.
  items.push({ key: "larger-text", label: "Larger text", icon: Type, run: toggleLargeText, group: 0, checked: largeText });
  items.push({
    key: "reduce-motion",
    label: "Reduce motion",
    icon: CirclePause,
    run: () => writeReduceMotionPreference(motionPerson, !reduceMotion),
    group: 0,
    checked: reduceMotion
  });
  // D-O115: the Admin's read-only "View as role" (the server honours it only for a real Admin), and the way back.
  if (viewAs) {
    items.push({ key: "view-as-stop", label: `Back to Admin (stop viewing as ${viewAs.roleLabel})`, icon: Undo2, run: () => void backToAdmin(), group: 0 });
  } else if (roles.includes(Roles.Admin)) {
    items.push({ key: "view-as", label: "View as role…", icon: Eye, run: () => setViewAsOpen(true), group: 0 });
  }
  if (canTestAs) {
    if (testing) items.push({ key: "test-as-stop", label: `Stop testing as ${testing.roleLabel}`, icon: Square, run: () => void stopTestAs(), group: 0 });
    items.push({ key: "test-as", label: testing ? "Test another role…" : "Test as role…", icon: FlaskConical, run: () => setTestAsOpen(true), group: 0 });
  }
  if (screens.includes("/about/release-notes")) items.push({ key: "release", label: "Release notes", icon: Sparkle, run: () => navigate("/about/release-notes"), group: 0 });
  if (screens.includes("/profile")) {
    items.push({ key: "profile", label: "My profile", icon: UserRound, run: () => navigate("/profile"), group: 1 });
    items.push({ key: "certificates", label: "My certificates", icon: Award, run: () => navigate("/profile#profile-certificates-table"), group: 1 });
  }
  if (canEndSigning) {
    items.push({
      key: "end-signing",
      label: "End signing session",
      icon: PenOff,
      run: () => {
        void headerApi
          .endSigningSession()
          .then((status) => {
            queryClient.setQueryData([...HEADER_QK.stepUp], status);
            setNotice("Signing session ended. The next step that needs your signature will ask for your authenticator code again.");
          })
          .catch(() => setNotice("The signing session could not be ended just now. Signing out ends it too."));
      },
      group: 2
    });
  }
  // Signing out ends a view-as session too (audited).
  items.push({
    key: "sign-out",
    label: "Sign out",
    icon: LogOut,
    // Signing out ends a view-as session and (for an Admin) any test session too, both audited, whatever this tab shows.
    run: () => {
      const viewing = readViewAs() !== null;
      // Only a real Admin can view as a role or test one; everyone else signs out at once.
      if (!viewing && !roles.includes(Roles.Admin)) return onSignOut();
      void (viewing ? stopViewAs() : Promise.resolve())
        .then(() => stopTestAs(false))
        .catch(() => undefined)
        .finally(() => onSignOut());
    },
    group: 2,
    tone: "danger"
  });

  useEffect(() => {
    const openViewAs = () => {
      setTestAsOpen(false);
      setViewAsOpen(true);
    };
    window.addEventListener("summitops:open-view-as", openViewAs);
    return () => window.removeEventListener("summitops:open-view-as", openViewAs);
  }, []);

  useEffect(() => {
    if (open) itemRefs.current[0]?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.parentElement?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), 8_000);
    return () => window.clearTimeout(t);
  }, [notice]);

  const close = (focus = true) => {
    setOpen(false);
    if (focus) buttonRef.current?.focus();
  };

  function onMenuKey(e: ReactKeyboardEvent) {
    const list = itemRefs.current.filter(Boolean) as HTMLButtonElement[];
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    const move = (i: number) => {
      e.preventDefault();
      list[(i + list.length) % list.length]?.focus();
    };
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === "ArrowDown") move(at + 1);
    else if (e.key === "ArrowUp") move(at - 1);
    else if (e.key === "Home") move(0);
    else if (e.key === "End") move(list.length - 1);
    else if (e.key === "Tab") close(false);
  }

  itemRefs.current = [];
  return (
    <div className="relative flex-none">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Account menu for ${name}`}
        title={`${name} — ${roleLabel}`}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className="flex items-center rounded-full p-0.5 transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
        data-testid="user-menu"
      >
        <Avatar name={name} roles={roles} size="sm" photo={photo} />
        <span className="sr-only" data-testid="signed-in-name">
          {name}
        </span>
      </button>
      {open &&
        createPortal(
        <div
          ref={menuRef}
          id={menuId}
          role="menu"
          aria-label={`Account menu for ${name}`}
          onKeyDown={onMenuKey}
          style={place}
          className="fixed z-50 overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-lg"
          data-testid="account-menu"
        >
          <div className="flex items-center gap-3 border-b border-slate-100 bg-slate-50/70 px-4 py-3" role="presentation">
            <Avatar name={name} roles={roles} size="md" photo={photo} />
            <div className="min-w-0 leading-tight">
              <div className="truncate text-sm font-semibold text-slate-900">{name}</div>
              <div className="truncate text-xs text-slate-600" data-testid="signed-in-roles">
                {roleLabel}
              </div>
              {upn && <div className="truncate text-[11px] text-slate-500">{upn}</div>}
            </div>
          </div>
          <div className="py-1">
            {items.map((item, i) => (
              <div key={item.key} role="none">
                {i > 0 && items[i - 1]!.group !== item.group && <div role="separator" className="my-1 border-t border-slate-100" />}
                <button
                  ref={(el) => {
                    itemRefs.current[i] = el;
                  }}
                  type="button"
                  role={item.checked === undefined ? "menuitem" : "menuitemcheckbox"}
                  aria-checked={item.checked === undefined ? undefined : item.checked}
                  tabIndex={-1}
                  onClick={() => {
                    // A switch stays open so the person sees the change and can switch it back.
                    if (item.checked === undefined) close(false);
                    item.run();
                  }}
                  className={`flex w-full items-center gap-2.5 px-4 py-1.5 text-left text-sm hover:bg-slate-50 focus:bg-brand-50 focus:outline-none ${
                    item.tone === "danger" ? "text-red-700" : "text-slate-700"
                  }`}
                  data-testid={`account-${item.key}`}
                >
                  <item.icon aria-hidden="true" className={`h-4 w-4 flex-none ${item.tone === "danger" ? "text-red-600" : "text-slate-500"}`} />
                  {item.label}
                  {item.checked !== undefined && (
                    <span
                      aria-hidden="true"
                      className={`ml-auto rounded-full border px-1.5 text-[11px] font-semibold ${
                        item.checked ? "border-brand-300 bg-brand-50 text-brand-700" : "border-slate-300 bg-white text-slate-600"
                      }`}
                    >
                      {item.checked ? "On" : "Off"}
                    </span>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>,
          document.body
        )}
      <div aria-live="polite" className="sr-only">
        {notice}
      </div>
      {notice &&
        !open &&
        createPortal(
          <p style={place} className="fixed z-50 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 shadow-lg" data-testid="account-notice">
            {notice}
          </p>,
          document.body
        )}
      <HeaderTour person={person} roles={roles} assignedRoles={assignedRoles} />
      <ViewAsBanner />
      <TestAsBanner enabled={canTestAs} />
      <TourOffer person={person} below={viewAs || testing ? 92 : 56} />
      {viewAsOpen && <ViewAsDialog onClose={() => setViewAsOpen(false)} />}
      {testAsOpen && <TestAsDialog onClose={() => setTestAsOpen(false)} />}
    </div>
  );
}
